import React from "react";
import AnnotationsContext from "../contexts/AnnotationsContext";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import ePub, { EpubCFI } from "epubjs";
import AnnotationRenderer from "../utils/annotator/AnnotationRenderer";
import {
  resolveHighlightColor,
  epubHighlightStyles,
} from "../utils/annotator/highlightColors";
import {
  isAnnotationHighlight,
  isPointNote,
} from "../utils/annotator/annotationsHelper";
import {
  collectOverridableSelectors,
  cssRulesFromViewerSettings,
  clearStylesheet,
  injectStylesheetRules,
} from "../utils/CssUtils";
import {
  cfiFromRange,
  cfiRangeFromCfiPair,
  rangesOverlap,
  rangeStartsAtOrBefore,
  rangeStartsBefore,
  expandRangeToWholeWords,
} from "../utils/CfiUtils";
import { AnnotationType } from "../utils/annotator/annotationsHelper";
import Highlighter from "../utils/Highlighter.js";
import { useTtsActions } from "../contexts/TtsContext";
import patchRendition from "../utils/patchRendition";
import "./EpubViewer.scss";
import {
  SearchDirection,
  findClosestMatchingElement,
  findMatchingElementInRange,
} from "../utils/DomUtils.js";
import { useFeature } from "../contexts/FeatureFlagContext";

const EMPTY_LOCATION = {
  start: { index: 0, cfi: "" },
  atStart: true,
  atEnd: true,
};

const NS_XHTML = "http://www.w3.org/1999/xhtml";

// How long a click on a highlight waits for a possible double-click (which
// starts TTS) before opening the note editor.
const HIGHLIGHT_EDIT_DELAY = 250;

export class EpubViewer extends React.Component {
  static contextType = AnnotationsContext;

  constructor(props) {
    super(props);
    this.viewerRef = React.createRef();
    this._prevBookmarks = [];
    this._prevNotes = [];
    this.state = {
      currentLocation: Object.assign({}, EMPTY_LOCATION),
      lastViewerSettings: null,
    };

    // All of this is local bookkeeping for wrapping epub.js. We're leaving it out of React
    // component state to avoid any overhead from change detection and rerendering.
    this.book = this.rendition = null;

    // Local bookkeeping for navigation info
    this.navigation = this.navigationBaseUrl = null;

    // TTS variables
    this.highlighter = new Highlighter({
      wordHighlightColor: props.viewerSettings.wordHighlightColor,
      sentenceHighlightColor: props.viewerSettings.sentenceHighlightColor,
    });
    this.ttsSessionStart = null;
    this.cfiComparator = new EpubCFI();
    this.cfiBase = null;

    // CSS variables
    this.sectionOverrideSelectors = {};

    this.handleArrowNextClick = this.handleArrowNextClick.bind(this);
    this.handleArrowPrevClick = this.handleArrowPrevClick.bind(this);

    this.handleRenditionRender = this.handleRenditionRender.bind(this);
    this.handleLocationUpdate = this.handleLocationUpdate.bind(this);
    this.handleContentDoubleClick = this.handleContentDoubleClick.bind(this);

    // Display sequence handling (for managing the loading spinner)
    this.handleDisplaySequenceStart =
      this.handleDisplaySequenceStart.bind(this);
    this.handleDisplaySequenceEnd = this.handleDisplaySequenceEnd.bind(this);

    // Error handling
    this.handleError = this.handleError.bind(this);

    // TTS
    this.onTtsStart = this.onTtsStart.bind(this);
    this.onTtsOutOfElements = this.onTtsOutOfElements.bind(this);
    this.onTtsOutOfPreviousElements =
      this.onTtsOutOfPreviousElements.bind(this);
    this.ttsHighlightSentence = this.ttsHighlightSentence.bind(this);
    this.handleAnnotationsInSentence =
      this.handleAnnotationsInSentence.bind(this);
    this.ttsHighlightWord = this.ttsHighlightWord.bind(this);
    this.ttsClearSentenceHighlight = this.ttsClearSentenceHighlight.bind(this);
    this.ttsClearWordHighlight = this.ttsClearWordHighlight.bind(this);
    this.ttsUpdateViewerPosition = this.ttsUpdateViewerPosition.bind(this);
    this.getTtsStartingNode = this.getTtsStartingNode.bind(this);
    this._ttsDomRegistered = false;

    // Hotkey handling
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);

    this.annotationRenderer = new AnnotationRenderer(this);

    // The previous sentence TTS spoke, used to detect when a point note is first crossed
    this.previousTtsSentenceRange = null;

    // Deferred open of the note editor for a clicked highlight; a double-click
    // (which starts TTS) cancels it before it fires
    this.highlightEditTimer = null;

    // Removes the inner-document mousemove listener that drives the highlight cursor
    this.highlightCursorCleanup = null;

    // Suppresses spurious selectionchange collapses that fire immediately after mouseup
    this.justMousedUpWithSelection = false;
  }

  componentDidMount() {
    console.log("EpubViewer mounted");

    // Add listener for mathjax render messages
    window.addEventListener("message", (event) => {
      if (
        event.origin === window.origin &&
        event.data === "mathjaxTypesetCompleted"
      ) {
        console.log("Mathjax typesetting complete");
      } else if (
        event.origin === window.origin &&
        event.data === "mathjaxInfoWindowOpened"
      ) {
        this.scrollMathJaxInfoWindowWithContent();
      }
    });

    // Load Book
    this.loadBook();
  }

  componentDidUpdate(prevProps) {
    if (this.props.viewerSettings !== prevProps.viewerSettings) {
      if (this.props.tts.isActive()) {
        this.ttsStop();
      }

      this.applyViewerSettings(this.props.viewerSettings);
    }

    if (this.rendition?.settings.flow === "scrolled") {
      const scrollContainer = this.rendition.views().container;
      if (scrollContainer) {
        scrollContainer.style.overflowX = "hidden";
      }
    }

    if (this.rendition) {
      const { bookmarks, notes } = this.context;
      const bookmarksChanged = bookmarks !== this._prevBookmarks;
      const notesChanged = notes !== this._prevNotes;

      if (bookmarksChanged || notesChanged) {
        this._prevBookmarks = bookmarks;
        this._prevNotes = notes;
        if (notesChanged) {
          this.renderHighlights();
        }
        this.annotationRenderer.renderAnnotations(bookmarks, notes);
      }
    }
  }

  componentWillUnmount() {
    if (this.highlightEditTimer) clearTimeout(this.highlightEditTimer);
    this.highlightCursorCleanup?.();
    if (this.book?.destroy) {
      this.book.destroy();
      this.book = this.rendition = null;
    }
  }

  // Compute the DOM node TTS should start reading from, based on the current viewer location.
  getTtsStartingNode() {
    if (!this.rendition) return null;
    const range = this.rendition.getRange(this.state.currentLocation.start.cfi);
    return range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer
      : range.startContainer.parentElement;
  }

  // Wired as registerDom's onStart so it runs on every fresh TtsContext.start().
  // Captures the user's current location as ttsSessionStart so earlier annotations
  // don't re-fire when the player reads from the top of the current paragraph.
  onTtsStart() {
    if (this.rendition) {
      const range = this.rendition.getRange(
        this.state.currentLocation.start.cfi,
      );
      this.ttsSessionStart = range;
      this.previousTtsSentenceRange = range;
    }
  }

  onTtsOutOfElements() {
    if (this.hasNext()) {
      this.ttsRelocating = true;
      this.rendition
        .next()
        .then(() => this.rendition.reportLocation())
        .then(() => this.props.tts.start());
    } else {
      this.props.tts.stop();
    }
  }

  onTtsOutOfPreviousElements() {
    if (this.hasPrevious()) {
      this.ttsRelocating = true;
      this.rendition
        .prev()
        .then(() => this.rendition.reportLocation())
        .then(() => this.props.tts.startAtEndOfSection());
    } else {
      this.props.tts.stop();
    }
  }

  ttsStop() {
    this.props.tts.stop();
    this.ttsSessionStart = null;
    this.previousTtsSentenceRange = null;
  }

  clearSelection() {
    const contents = this.rendition.getContents();
    if (contents.length > 0) {
      contents[0].window.getSelection()?.removeAllRanges();
    }
  }

  ttsHighlightWord(range) {
    this.highlighter.highlightWord(
      range,
      this.props.viewerSettings.wordHighlightColor,
    );
  }

  ttsHighlightSentence(range) {
    this.highlighter.highlightSentence(
      range,
      this.props.viewerSettings.sentenceHighlightColor,
    );
  }

  /**
   * Called by the TTS player just before a sentence is spoken. Returns overlapping
   * annotations when readAnnotations is true, or null when false.
   * @param {Range} range
   * @returns {AnnotationOverlaps | null}
   */
  handleAnnotationsInSentence(range) {
    const overlaps = this.findAnnotationsOverlappingSentence(range);
    this.previousTtsSentenceRange = range;
    if (!this.props.viewerSettings.readAnnotations) {
      return null;
    }
    return overlaps;
  }

  /**
   * Gathers the bookmark and highlight (one each) and every note whose location overlaps the
   * given sentence range, drawing the current annotations and TTS session state straight from
   * the component. Bookmarks whose start lies before the TTS session start are excluded from
   * "latest crossed" detection, so bookmarks the user already passed before TTS started don't
   * re-fire. Point notes are zero-width like bookmarks, so they're detected by crossing rather
   * than overlap; a point note fires on the first sentence that reaches it (even if it sits in a
   * gap between sentence ranges) and then releases.
   * @param {Range} sentenceRange
   * @returns {import("../utils/annotator/annotationsHelper").AnnotationOverlaps} Ready to pass
   *   straight into AnnotationChime.dispatch(); `notes` is ordered earliest-first for TTS.
   */
  findAnnotationsOverlappingSentence(sentenceRange) {
    const { highlight, notes } = this.findMatchingNotes(sentenceRange);
    return {
      [AnnotationType.Bookmark]: this.findLatestCrossedBookmark(sentenceRange),
      [AnnotationType.Highlight]: highlight,
      notes,
    };
  }

  /** Resolves an annotation's CFI location to a DOM Range, or null if it can't be placed. */
  resolveRange(location) {
    try {
      return this.rendition.getRange(location) ?? null;
    } catch (ex) {
      return null;
    }
  }

  /**
   * True when a zero-width point (e.g. a point note) is first reached by the current sentence:
   * at or before the sentence's end, and not already passed by the previous sentence (read from
   * this.previousTtsSentenceRange). Unlike a plain overlap test it still fires for a point that
   * sits in a gap between sentence ranges; unlike the bookmarks' "latest crossed" scan it releases
   * after one sentence, so a point note registers once as it's reached rather than staying in scope
   * while reading continues past it. On the first sentence of a session there is no previous range,
   * so only the upper bound applies.
   */
  pointReachedThisSentence(point, sentenceRange) {
    if (!rangeStartsAtOrBefore(point, sentenceRange)) return false;
    const previousSentenceRange = this.previousTtsSentenceRange;
    if (
      previousSentenceRange &&
      rangeStartsAtOrBefore(point, previousSentenceRange)
    ) {
      return false;
    }
    return true;
  }

  /**
   * Finds the most recently crossed bookmark among this.context.bookmarks. Bookmarks are point
   * locations, "reached" when their position is at or before the current sentence's end. They come
   * in document order, so the last crossed bookmark is the most recent; pair this with the
   * dispatcher's location-based debouncing to fire the chime exactly once per bookmark. Bookmarks
   * whose start lies before the TTS session start (this.ttsSessionStart) are skipped, so bookmarks
   * the user already passed before TTS started don't re-fire.
   */
  findLatestCrossedBookmark(sentenceRange) {
    const lowerBound = this.ttsSessionStart;
    let latest = null;
    for (const bookmark of this.context.bookmarks) {
      const range = this.resolveRange(bookmark.location);
      if (!range) continue;
      if (lowerBound && rangeStartsBefore(range, lowerBound)) continue;
      if (rangeStartsAtOrBefore(range, sentenceRange)) latest = bookmark;
    }
    return latest;
  }

  /**
   * Finds the first highlight and every note among this.context.notes whose location matches the
   * sentence. Highlights are notes without a `note` value; only the first is needed since highlights
   * are not read aloud. Notes (with text) are all collected and returned sorted by location (earliest
   * first) so TTS can read them in reading order when more than one falls in the same sentence. A
   * point note has no span (collapsed range), so like a bookmark it sits at a single point; detect it
   * by crossing rather than overlap so a note that lands in a gap between sentence ranges still fires.
   * Span notes (highlights and range notes) use overlap.
   */
  findMatchingNotes(sentenceRange) {
    let highlight = null;
    const matchedNotes = [];
    for (const annotation of this.context.notes) {
      const isHighlight = isAnnotationHighlight(annotation);
      if (isHighlight && highlight) continue;
      const range = this.resolveRange(annotation.location);
      if (!range) continue;
      const matched = range.collapsed
        ? this.pointReachedThisSentence(range, sentenceRange)
        : rangesOverlap(sentenceRange, range);
      if (!matched) continue;
      if (isHighlight) highlight = annotation;
      else matchedNotes.push({ annotation, range });
    }
    const notes = matchedNotes
      .sort((a, b) =>
        a.range.compareBoundaryPoints(Range.START_TO_START, b.range),
      )
      .map((match) => match.annotation);
    return { highlight, notes };
  }

  ttsClearWordHighlight() {
    this.highlighter.clearWordHighlight();
  }

  ttsClearSentenceHighlight() {
    this.highlighter.clearSentenceHighlight();
  }

  ttsUpdateViewerPosition(range) {
    // epub.js has trouble with element-based offsets. If both range endpoint offsets are
    // in element containers, then range -> CFI -> range conversion breaks. Proceed only
    // if one of the endpoints is a text node.

    if (
      range.startContainer.nodeType === Node.TEXT_NODE ||
      range.endContainer.nodeType === Node.TEXT_NODE
    ) {
      const cfi = cfiFromRange(range, this.cfiBase).toString();

      // Move if cfi is *before* the start, or *after* the end.
      if (
        this.cfiComparator.compare(
          cfi,
          this.state.currentLocation.start.cfi,
        ) === -1 ||
        this.cfiComparator.compare(cfi, this.state.currentLocation.end.cfi) ===
          1
      ) {
        this.ttsRelocating = true;
        this.rendition.display(cfi).then(() => {
          this.ttsRelocating = false;
        });
      }
    }
  }

  handleKeyDown(event) {
    this.props?.hotkeyManager?.handleKeyDown(event);
  }

  handleKeyUp(event) {
    this.props?.hotkeyManager?.handleKeyUp(event);
    if (event.key === "Shift") {
      const selection = event.target?.ownerDocument?.getSelection?.();
      expandRangeToWholeWords(selection.getRangeAt(0));
    }
  }

  loadBook() {
    // destroy any existing book
    if (this.book && this.book.destroy) {
      this.book.destroy();
      this.book = this.rendition = null;
      this.setState({ currentLocation: Object.assign({}, EMPTY_LOCATION) });
    }

    // todo: pass through any existing device-level display settings
    const rootElement = this.viewerRef.current;
    const book = new ePub.Book({ requestCredentials: true });
    this.book = book;

    // Hook into book load handlers
    book.loaded.metadata.then(
      (metadata) =>
        this.props.onMetadataLoaded && this.props.onMetadataLoaded(metadata),
    );
    book.loaded.spine.then(
      (spine) => this.props.onSpineLoaded && this.props.onSpineLoaded(spine),
    );
    book.loaded.navigation.then((navigation) => {
      this.navigation = navigation;

      // Include base URL of navigation document. All spine items are relative to this path.
      const navPath = book.packaging.navPath;
      this.baseUrl =
        navPath.indexOf("/") >= 0
          ? navPath.substring(0, navPath.lastIndexOf("/") + 1)
          : "";
      this.props.onNavigationLoaded &&
        this.props.onNavigationLoaded(navigation, this.baseUrl);
      this.props.onPageListLoaded && this.props.onPageListLoaded(book.pageList);
    });
    // Open the book
    book
      .open(`/epub/${this.props.bookshareId}/`)
      .then(() => {
        this.rendition = book.renderTo(rootElement, {
          flow: this.props.bookHasMath
            ? "scrolled"
            : this.props.viewerSettings.flow,
          height: "100%",
          axis: "vertical",
          allowPopups: true,
          allowScriptedContent: true,
        });

        //Hook fires exactly once when the section is accessed
        this.book.spine.hooks.content.register((document, section) => {
          // Inject custom font definitions
          this.attachWebFontCss(document);

          // Inject MathJax code if needed
          if (section.properties.includes("mathml")) {
            console.log("Attaching mathjax support");
            this.attachMathjaxSupport(document);
          }
        });
        //Hook fires every time the section is accessed
        this.rendition.hooks.content.register((contents, rendition) => {
          if (!this.sectionOverrideSelectors[contents.sectionIndex]) {
            this.sectionOverrideSelectors[contents.sectionIndex] =
              collectOverridableSelectors(contents.document);
          }
          return this.applyViewerSettings(this.props.viewerSettings);
        });
        this.rendition.on("rendered", this.handleRenditionRender);
        this.rendition.on("relocated", this.handleLocationUpdate);

        // All of these events should display the spinner
        this.rendition.on("resizeRequested", this.handleDisplaySequenceStart);
        this.rendition.on("displayRequested", this.handleDisplaySequenceStart);
        this.rendition.on("nextRequested", this.handleDisplaySequenceStart);
        this.rendition.on("prevRequested", this.handleDisplaySequenceStart);

        // All of these events should dismiss the spinner
        this.rendition.on("displayed", this.handleDisplaySequenceEnd);
        this.rendition.on("nextCompleted", this.handleDisplaySequenceEnd);
        this.rendition.on("prevCompleted", this.handleDisplaySequenceEnd);

        // Catches any errors triggered by rendition.display, rendition.next, and rendition.prev calls.
        this.rendition.on("displayerror", this.handleError);
        this.rendition.on("nextError", this.handleError);
        this.rendition.on("prevError", this.handleError);

        // Watch for selection events
        this.rendition.on("selected", (cfiRange) => {
          this.props.onTextSelectionChanged?.(cfiRange);
        });

        // Apply monkey patches
        patchRendition(this.rendition);

        if (process.env.NODE_ENV !== "production") {
          window.rendition = this.rendition;
          window.epubViewer = this;
        }
      })
      // on book load we need to get the location from either the server or local storage
      // but need to do it here because the rendition needs to be created and ready first
      .then(() => this.props.loadLastLocation())
      .then((location) => {
        if (this.rendition) {
          return location
            ? this.rendition.display(location, true)
            : this.rendition.display();
        }
      })
      // Make the scrollable div container focusable
      .then(() => {
        const views = this.rendition.views();
        views.container.tabIndex = 0;
        views.container.ariaLabel = "Book content";
      })

      .catch((e) => {
        this.handleError(e);
      });
  }

  /**
   * Attaches web fonts CSS to the provided document
   * @param {Document} document
   */
  attachWebFontCss(document) {
    const css = document.createElementNS(NS_XHTML, "link");
    css.setAttribute("type", "text/css");
    css.setAttribute("rel", "stylesheet");
    css.setAttribute(
      "href",
      `${process.env.PUBLIC_URL}/fonts/fontDefinitions.css`,
    );
    document.head.appendChild(css);
  }

  /**
   * Attaches MathJax script and CSS to the provided document.
   * @param {Document} document
   */
  attachMathjaxSupport(document) {
    const script = document.createElementNS(NS_XHTML, "script");
    script.setAttribute("src", `${process.env.PUBLIC_URL}/scripts/Mathjax.js`);
    script.setAttribute("type", "text/javascript");
    document.head.appendChild(script);
  }

  /**
   * Locates or creates the stylesheet we will use to apply user viewer settings.
   * @param {Document} document
   * @returns {CSSStyleSheet}
   */
  findOrCreateViewerSettingsStyleSheet(document) {
    const styleId = "bks-viewer-styles";
    for (const styleSheet of document.styleSheets) {
      // TODO: Is there a better (Firefox-supported) method for detecting CSSStyleRules?
      if (styleSheet.ownerNode && styleSheet.ownerNode.id === styleId) {
        return styleSheet;
      }
    }
    const styleBlock = document.createElement("style");
    styleBlock.id = styleId;
    styleBlock.setAttribute("type", "text/css");
    document.head.appendChild(styleBlock);
    return this.findOrCreateViewerSettingsStyleSheet(document);
  }

  /**
   * Injects stylesheet rules based on ViewerSettings into the rendition's active Views.
   * @param {ViewerSettings} settings Viewer settings
   * @returns {Promise} Either the rendition.display Promise or an immediately-resolved Promise.
   */
  applyViewerSettings(settings) {
    console.log("applying settings");
    console.log(settings);

    // Default promise return value
    let promise = new Promise((resolve, reject) => resolve());

    const layoutShift =
      this.state.lastViewerSettings &&
      !settings.layoutEquals(this.state.lastViewerSettings);
    const colorChange =
      this.state.lastViewerSettings &&
      !settings.colorEquals(this.state.lastViewerSettings);

    const isFixedLayout =
      (this.props.metadata && this.props.metadata.layout === "pre-paginated") ||
      false;

    // Proceed only if rendition has been initialized
    if (this.rendition) {
      const currentLocation = this.state.currentLocation.start.cfi;
      console.log(currentLocation);

      // Flow setting is special
      if (
        this.props.bookHasMath &&
        this.rendition.settings.flow !== "scrolled"
      ) {
        //Mathjax's context menu does not work well with paginated view - we want to only allow scrolling mode for these books.
        this.enableScrollingFlow();
      } else if (isFixedLayout) {
        if (this.rendition.settings.flow !== "paginated") {
          this.rendition.spread("auto");
          this.enablePaginatedFlow();
        }
      } else if (
        this.rendition.settings.flow !== settings.flow &&
        !this.props.bookHasMath
      ) {
        switch (settings.flow) {
          case "scrolled":
            this.enableScrollingFlow();
            break;
          default:
            this.enablePaginatedFlow();
        }
      }

      // So are highlighter colors
      this.highlighter.sentenceHighlightColor = settings.sentenceHighlightColor;
      this.highlighter.wordHighlightColor = settings.wordHighlightColor;

      // Okay, speech synthesis too
      this.applyVoices(settings);

      // The rest are just CSS rules
      if (!isFixedLayout) {
        this.rendition.views().forEach((v) => {
          // Generate stylesheet rules
          const rules = cssRulesFromViewerSettings(
            settings,
            this.sectionOverrideSelectors[v.section.index],
          );

          // Delete any existing rules
          const doc = v.contents.document;
          const injectedStyles = this.findOrCreateViewerSettingsStyleSheet(doc);
          clearStylesheet(injectedStyles);

          // Insert the rules
          injectStylesheetRules(injectedStyles, rules);

          v.contents.resizeCheck();
        });

        if (layoutShift) {
          promise = this.rendition.display(
            this.state.currentLocation.start.cfi,
          );
        }
      }
    }

    // Put viewer settings in state
    this.setState({ lastViewerSettings: settings });

    // Once the display settles, clear and redraw all annotation icons
    return promise.then(() => {
      if (layoutShift || colorChange) {
        this.rerenderAnnotations();
      }
    });
  }

  applyVoices(settings) {
    this.props.tts.updateSettings({
      voice:
        this.props.voices.find((v) => v.voiceURI === settings.voice) ?? null,
      rate: settings.audioSpeed,
      voiceImageDescriptions: settings.voiceImageDescriptions,
      voicePageNumbers: settings.voicePageNumbers,
    });
  }

  isValidSelection(selection) {
    return (
      selection &&
      selection.rangeCount > 0 &&
      !selection.getRangeAt(0).collapsed &&
      selection.toString().trim() !== ""
    );
  }

  /**
   * @param {Section} section
   * @param {View} view
   */
  handleRenditionRender(section, view) {
    console.log(`Finished render of section ${section.index}`);
    const doc = view.document;

    // Keydown listeners
    doc.documentElement.addEventListener("keydown", this.handleKeyDown, {
      passive: false,
    });
    doc.documentElement.addEventListener("keyup", this.handleKeyUp, {
      passive: false,
    });

    // Lame workaround for the lack of event triggering when selections are cleared
    doc.addEventListener("selectionchange", (e) => {
      if (this.justMousedUpWithSelection) return;
      const selection = e.target.getSelection();
      if (selection.rangeCount === 0 || selection.getRangeAt(0).collapsed) {
        this.props.onTextSelectionChanged &&
          this.props.onTextSelectionChanged(null);
      }
    });

    // Prevent select on double-click
    doc.addEventListener("mousedown", (e) => {
      this.justMousedUpWithSelection = false;
      if (e.detail > 1) {
        e.preventDefault();
      }
    });

    // Show the NoteButton anchored to the selection once the user finishes selecting
    doc.addEventListener("mouseup", () => {
      const sel = doc.getSelection();
      if (this.isValidSelection(sel)) {
        expandRangeToWholeWords(sel.getRangeAt(0));
        this.justMousedUpWithSelection = true;
        setTimeout(() => {
          this.justMousedUpWithSelection = false;
        }, 0);
        const rects = this.viewportRectsFromRange(sel.getRangeAt(0));
        this.props.onTextSelectionMouseup?.(rects?.getViewportRect ?? null);
      }
    });

    // Attach double-click handler to the ViewManager document
    doc.addEventListener("dblclick", this.handleContentDoubleClick);

    // Update TTS player root (or register on first render)
    if (!this._ttsDomRegistered) {
      this.props.tts.registerDom({
        root: view.document.body,
        highlightSentence: this.ttsHighlightSentence,
        highlightWord: this.ttsHighlightWord,
        clearSentenceHighlight: this.ttsClearSentenceHighlight,
        clearWordHighlight: this.ttsClearWordHighlight,
        updateViewerPosition: this.ttsUpdateViewerPosition,
        getStartingNode: this.getTtsStartingNode,
        onOutOfElements: this.onTtsOutOfElements,
        onOutOfPreviousElements: this.onTtsOutOfPreviousElements,
        onTtsStart: this.onTtsStart,
        handleAnnotationsInSentence: this.handleAnnotationsInSentence,
      });
      this._ttsDomRegistered = true;
      // The content hook may have called applyViewerSettings → tts.updateSettings
      // before the player existed; re-push current settings so voice/rate stick.
      const settings = this.props.viewerSettings;
      this.applyVoices(settings);
    } else {
      this.props.tts.setRoot(view.document.body);
    }

    // Hang on to cfiBase of current section for position comparisons
    this.cfiBase = section.cfiBase;

    // Redraw all annotation icons for the newly displayed section
    this.rerenderAnnotations();

    // Render highlights (each gets a click-to-edit handler) and show a pointer
    // cursor while hovering them
    this.renderHighlights();
    this.setupHighlightCursor();

    // Set title on iframe
    if (view.iframe) {
      view.iframe.title = this.book.packaging.metadata.title;
    }

    this.props.onBookRendered && this.props.onBookRendered(section, view);
  }

  /**
   * @param {*} newLocation
   */
  handleLocationUpdate(newLocation) {
    this.setState({ currentLocation: newLocation });

    const d = newLocation.start.displayed;

    // See if we should preload the next section
    if (this.hasNext() && d.page / d.total >= 0.8) {
      this.book.section(newLocation.start.index + 1).load();
    }

    // If TTS is _not_ running, clear any active data to account for the change in location
    if (!this.props.tts.isActive()) {
      this.props.tts.reset();
    }

    // Find the node containing the start of the visible content.
    const startNode = this.rendition.getRange(
      newLocation.start.cfi,
    ).startContainer;

    // Try to find the closest preceding page break element
    const pageBreak = findClosestMatchingElement(
      startNode,
      (e) =>
        e.getAttribute("epub:type") === "pagebreak" ||
        e.getAttribute("role") === "doc-pagebreak",
      SearchDirection.BACKWARD,
    );

    // Extract the text, giving preference to title over aria-label
    let pageTitle = pageBreak && (pageBreak.title || pageBreak.ariaLabel);

    // Strip off anything that isn't arabic or roman numerals.
    // TODO: Revisit for multi-lingual support
    pageTitle = pageTitle?.match(/\b[0-9MDCLXVImdclxvi]+\b/)?.[0];

    // Find closest TOC item. The navigation list's hrefs are relative to the navigation
    // document, while the epub.js location object contains the full path within the publication.
    // We therefore need to normalize URLs relative to the navigation document's base URL.
    const normalizedHref = newLocation.start.href.replace(this.baseUrl, "");
    const tocMatcher = (e) =>
      e.id && `${normalizedHref}#${e.id}` in this.navigation.tocByHref;

    const visibleRange = this.rendition.getRange(
      cfiRangeFromCfiPair(newLocation.start.cfi, newLocation.end.cfi),
    );

    // The base fallback href is always going to be the bare normalized href.
    let tocHref = normalizedHref;

    // This condition might be tough to grok. If page === 1, we are at the top of the section.
    // If the bare section HREF is in the TOC, then this top position *is* a TOC entry, so we can stop.
    //
    // If either of those is false, we need to search for an ID that corresponds to a TOC entry, starting
    // with the displayed range, then trying a backwards search.
    if (
      newLocation.start.displayed.page !== 1 ||
      !(tocHref in this.navigation.tocByHref)
    ) {
      const tocElement =
        findMatchingElementInRange(visibleRange, tocMatcher) ||
        findClosestMatchingElement(
          startNode,
          tocMatcher,
          SearchDirection.BACKWARD,
        );

      if (tocElement) {
        tocHref += `#${tocElement.id}`;
      }
    }

    this.props.onRenditionRelocated &&
      this.props.onRenditionRelocated(newLocation, tocHref, pageTitle);
  }

  /**
   * Fires when a display sequence is started. This can be the result of:
   * - rendition.display
   * - rendition.next
   * - rendition.prev
   */
  handleDisplaySequenceStart() {
    // If the tts is relocating (see updateViewerPosition) don't stop tts.
    if (!this.ttsRelocating) {
      this.ttsStop();
    }
    this.props.onDisplaySequenceStart && this.props.onDisplaySequenceStart();
  }

  handleDisplaySequenceEnd() {
    const contents = this.rendition.getContents();
    if (contents.length > 0) {
      this.rendition.getContents()[0].css("visibility", "visible", true);
    }
    this.props.onDisplaySequenceEnd && this.props.onDisplaySequenceEnd();
  }

  /**
   * @param {MouseEvent} event
   */
  handleContentDoubleClick(event) {
    // A double-click starts TTS, so cancel any pending highlight-edit opener
    // queued by the first click.
    if (this.highlightEditTimer) {
      clearTimeout(this.highlightEditTimer);
      this.highlightEditTimer = null;
    }
    const doc = event.target.ownerDocument;
    const sessionStart = doc.createRange();
    sessionStart.setStart(event.target, 0);
    sessionStart.setEnd(event.target, 0);
    this.ttsSessionStart = sessionStart;
    this.previousTtsSentenceRange = sessionStart;
    this.props.tts.startFromMouseEvent(event);
  }

  handleError(error) {
    const onErrorRaised = this.props.onErrorRaised;
    onErrorRaised && onErrorRaised(error);
  }

  /**
   * Switch to paginated mode.
   */
  enablePaginatedFlow() {
    this.rendition.flow("paginated");
  }

  /**
   * Switch to scrolling mode.
   */
  enableScrollingFlow() {
    this.rendition.flow("scrolled");
  }

  handleArrowNextClick() {
    this.rendition.next();
  }

  handleArrowPrevClick() {
    this.rendition.prev();
  }

  /**
   * Returns true if the 'next' arrow button should be enabled.
   */
  hasNext() {
    if (this.rendition?.settings.flow === "paginated") {
      return !this.state.currentLocation.atEnd;
    } else {
      return (
        this.book &&
        this.book.spine &&
        this.state.currentLocation.start.index + 1 < this.book.spine.length
      );
    }
  }

  /**
   * Returns true if the 'previous' arrow button should be enabled.
   */
  hasPrevious() {
    if (this.rendition?.settings.flow === "paginated") {
      return !this.state.currentLocation.atStart;
    } else {
      return this.state.currentLocation.start.index > 0;
    }
  }

  /**
   * Given a DOM Range inside the epub iframe, returns { getViewportRect }: a live
   * callback yielding the range's rect in host-page viewport coordinates. Returns
   * null if the range cannot be resolved.
   * @param {Range} range
   */
  viewportRectsFromRange(range) {
    try {
      const c = this.rendition.views()._views[0].contents;
      const rect = range.getBoundingClientRect();
      const iframeEl = c.window.frameElement;

      const contentTop = rect.top + c.window.scrollY;
      const contentLeft = rect.left + c.window.scrollX;

      const getViewportRect = () => {
        const ir = iframeEl
          ? iframeEl.getBoundingClientRect()
          : { top: 0, left: 0 };
        return {
          top: ir.top + contentTop - c.window.scrollY,
          left: ir.left + contentLeft - c.window.scrollX,
          width: rect.width,
          height: rect.height,
        };
      };

      return { getViewportRect };
    } catch (e) {
      return null;
    }
  }

  /**
   * Convenience function to create new epub CFI
   * @param {Range} range
   * @returns {EpubCFI} CFI
   */
  cfiFromRange(range) {
    return cfiFromRange(range, this.cfiBase);
  }

  scrollMathJaxInfoWindowWithContent() {
    const contents = this.rendition.getContents()[0];
    const contextMenu =
      contents.document.getElementsByClassName("CtxtMenu_Info")[0];
    if (contextMenu) {
      contextMenu.style.top =
        this.rendition.manager.scrollTop +
        (this.rendition.manager.viewSettings.height -
          contextMenu.clientHeight) /
          2 +
        "px";
    }
  }

  // Clear and redraw all annotation icons from the current context. Used after a layout or
  // section change, when icon positions are stale and must be recomputed rather
  // than reconciled.
  rerenderAnnotations() {
    this.annotationRenderer.clearAndRerenderAnnotations(
      this.context.bookmarks,
      this.context.notes,
    );
  }

  renderHighlights() {
    // The color each currently-rendered highlight was drawn in, keyed by location.
    const highlightsToRemove = {};
    Object.keys(this.rendition.annotations._annotations)
      .filter((key) => key.endsWith("highlight"))
      .forEach((key) => {
        const location = decodeURI(
          key.substring(0, key.lastIndexOf("highlight")),
        );
        highlightsToRemove[location] =
          this.rendition.annotations._annotations[key].styles?.fill;
      });

    // The color each note should be drawn in, normalized to the palette.
    const highlightsToDraw = {};
    this.context.notes.forEach((note) => {
      highlightsToDraw[note.location] = resolveHighlightColor(note.color);
    });

    // A color change is handled as a remove followed by a re-draw, so both
    // passes key off the same "desired color differs from rendered color" test.
    Object.keys(highlightsToRemove)
      .filter(
        (location) =>
          highlightsToDraw[location] !== highlightsToRemove[location],
      )
      .forEach((location) =>
        this.rendition.annotations.remove(location, "highlight"),
      );

    Object.keys(highlightsToDraw)
      .filter(
        (location) =>
          highlightsToDraw[location] !== highlightsToRemove[location],
      )
      .forEach((location) =>
        this.rendition.annotations.highlight(
          location,
          {},
          () => this.handleHighlightClicked(location),
          "epubjs-hl",
          epubHighlightStyles(highlightsToDraw[location]),
        ),
      );
  }

  pointInRect(rect, iframeRect, x, y) {
    const top = rect.top - iframeRect.top;
    const left = rect.left - iframeRect.left;
    return (
      top <= y && left <= x && y < top + rect.height && x < left + rect.width
    );
  }

  /**
   * Handles a click on a highlight. A click may be the first half of a
   * double-click that starts TTS, so opening the editor is deferred briefly;
   * handleContentDoubleClick cancels the timer when a double-click is
   * recognized. A lone click (no double-click within the window) opens the
   * editor once the timer fires. The note is looked up fresh by location so
   * edits to its text are always reflected. Point notes are reached through
   * their margin icon, not the highlight span, so they are ignored here.
   * @param {string} location CFI range of the clicked highlight
   */
  handleHighlightClicked(location) {
    if (this.highlightEditTimer) clearTimeout(this.highlightEditTimer);
    this.highlightEditTimer = setTimeout(() => {
      this.highlightEditTimer = null;
      const note = this.context.notes.find((n) => n.location === location);
      if (note && !isPointNote(note)) this.openNoteEditor(note);
    }, HIGHLIGHT_EDIT_DELAY);
  }

  /**
   * Opens a note in the edit modal. Point notes and highlight/range notes alike
   * are edited in a centered NoteModal, so the note is passed straight to the
   * click handler with no anchoring rect. Called by the highlight click handler
   * and by the point-note margin icon's click handler (via AnnotationRenderer).
   * @param {Annotation} note The note to edit
   */
  openNoteEditor(note) {
    this.props.onNoteClick?.(note);
  }

  /**
   * Shows a pointer cursor while the mouse is over a highlight so users see it
   * is clickable. The overlay stays pointer-events:none (so it never blocks
   * text selection or the native double-click that starts TTS), which also
   * means it can't carry the cursor itself — so the cursor is toggled on the
   * inner document, driven by a mousemove hit-test. marks-pane does not proxy
   * mousemove, so the listener is attached to the inner document directly.
   */
  setupHighlightCursor() {
    this.highlightCursorCleanup?.();
    this.highlightCursorCleanup = null;

    const view = this.rendition.views()._views[0];
    const innerDocument = view?.iframe?.contentDocument;
    const iframeElement = view?.iframe;
    if (!innerDocument || !iframeElement) return;

    let overHighlight = false;
    const onMouseMove = (event) => {
      const iframeRect = iframeElement.getBoundingClientRect();
      const isOver = !!this.findHoveredHighlight(
        view,
        iframeRect,
        event.clientX,
        event.clientY,
      );
      if (isOver === overHighlight) return;
      overHighlight = isOver;
      innerDocument.documentElement.style.cursor = isOver ? "pointer" : "";
    };

    innerDocument.addEventListener("mousemove", onMouseMove);
    this.highlightCursorCleanup = () =>
      innerDocument.removeEventListener("mousemove", onMouseMove);
  }

  /**
   * Returns the first non-point-note annotation whose highlight contains the
   * point (in inner-document coordinates), or null. Point notes have no visible
   * highlight, so they are skipped.
   */
  findHoveredHighlight(view, iframeRect, x, y) {
    return (
      this.context.notes.find((note) => {
        if (isPointNote(note)) return false;
        const mark = view.highlights?.[note.location]?.mark;
        return mark && this.isPointInMark(mark, iframeRect, x, y);
      }) ?? null
    );
  }

  /**
   * True when the point lies within any of the mark's client rectangles,
   * including the inter-line gaps of a multi-line highlight so the cursor does
   * not flicker between lines.
   */
  isPointInMark(mark, iframeRect, x, y) {
    // Fast path: skip the per-rect walk if the point is outside the bounding box.
    if (!this.pointInRect(mark.getBoundingClientRect(), iframeRect, x, y)) {
      return false;
    }

    const rects = Array.from(mark.getClientRects()).sort(
      (a, b) => a.top - b.top,
    );
    for (let i = 0; i < rects.length; i++) {
      if (this.pointInRect(rects[i], iframeRect, x, y)) return true;
      const next = rects[i + 1];
      if (!next) continue;
      const gapRect = {
        top: rects[i].top + rects[i].height,
        left: Math.min(rects[i].left, next.left),
        height: next.top - (rects[i].top + rects[i].height),
        width:
          Math.max(rects[i].left + rects[i].width, next.left + next.width) -
          Math.min(rects[i].left, next.left),
      };
      if (this.pointInRect(gapRect, iframeRect, x, y)) return true;
    }
    return false;
  }

  render() {
    return (
      <div
        className="d-flex flex-column flex-grow-1 overflow-hidden"
        role="main"
      >
        {/* epub.js mounts the book content into this element, appending its
            container as the last child. Keeping it as a dedicated mount node
            lets the book content stay before the navigation arrows in source
            order so assistive-technology users reach the content first. */}
        <div
          ref={this.viewerRef}
          className="epubViewer flex-grow-1 overflow-auto"
          style={{
            backgroundColor: this.props.viewerSettings.backgroundColor,
            "--highlight-blend-mode": "normal",
          }}
        />
        {/* The arrows are position: fixed, so their source-order position does
            not change where they render on screen. They come after the book
            content but stay inside the main landmark for assistive technology. */}
        <Button
          variant="link"
          className="viewerArrow viewerArrowPrev"
          disabled={!this.hasPrevious()}
          onClick={this.handleArrowPrevClick}
          aria-label="Previous"
        >
          <FontAwesomeIcon icon={faAngleLeft} size="3x" role="presentation" />
        </Button>
        <Button
          variant="link"
          className="viewerArrow viewerArrowNext"
          disabled={!this.hasNext()}
          onClick={this.handleArrowNextClick}
          aria-label="Next"
        >
          <FontAwesomeIcon icon={faAngleRight} size="3x" role="presentation" />
        </Button>
      </div>
    );
  }
}

const EpubViewerWithTts = React.forwardRef(
  function EpubViewerWithTts(props, ref) {
    // EpubViewer only triggers playback actions (start/stop/registerDom/etc.)
    // and never reads state or location, so it subscribes to actions only and
    // does not re-render as TTS progresses through the book.
    const tts = useTtsActions();
    const annotationsEnabled = useFeature("annotations");
    return (
      <EpubViewer
        {...props}
        tts={tts}
        annotationsEnabled={annotationsEnabled}
        ref={ref}
      />
    );
  },
);

export default EpubViewerWithTts;
